import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { environment } from '../../../environments/environment';

declare let gtag: Function;

/**
 * What caused a provider search. `initial` and `program_page` are the automatic search on page
 * load (the latter carrying programs chosen on the Program page); every other value is a student
 * using a control. Reports exclude the automatic ones when counting how often a feature is used.
 */
export type SearchTrigger =
    | 'initial'
    | 'program_page'
    | 'submit'
    | 'program'
    | 'distance'
    | 'clear_text'
    | 'clear_address'
    | 'remove_program';

export interface SearchAnalytics {
    trigger: SearchTrigger;
    searchTerm: string;
    searchedByAddress: boolean;
    searchDistance: number;
    programIds: number[];
}

@Injectable({
    providedIn: 'root'
})
export class GoogleAnalyticsService {
    /*
        GA4

        When choosing a name for a new event, follow these rules:
        - Event names are case sensitive. For example, my_event and My_Event are distinct events.
        - Event names can include English and non-English words and letters.
        - Do not use reserved prefixes and event names.
        - Event names must start with a letter. Use only letters, numbers, and underscores. Don't use spaces.

        If adding new events, be sure to include in "studentportal.literacypro.com" container on https://tagmanager.google.com/.

    */
    constructor() {}

    public pageView(pageTitle: string = '') {
        gtag('event', 'page_view', {
            ...this.getCommonProperties(),
            page_title: pageTitle
        });
    }

    /**
     * Reports a provider search, so the provider list's filters can be measured feature by
     * feature (LA-23200).
     *
     * `searchedByAddress` says whether the student filtered by address. The address itself is
     * deliberately not sent — it is typically their home address, and the useful signal is how
     * often location search is used, not where anyone lives. `searchDistance` is the radius in
     * miles, 0 when no distance filter is applied.
     *
     * Programs are sent as ids, not names: names differ by language and can be overridden per
     * client, so one program would show up as several values. Ids are client configuration, not
     * student data. They are sorted so the same selection always reports the same value.
     */
    public search(pageTitle: string, criteria: SearchAnalytics) {
        gtag('event', 'search', {
            ...this.getCommonProperties(),
            page_title: pageTitle,
            search_trigger: criteria.trigger,
            search_term: criteria.searchTerm,
            searched_by_text: criteria.searchTerm.trim().length > 0,
            searched_by_address: criteria.searchedByAddress,
            search_distance: criteria.searchDistance,
            searched_by_program: criteria.programIds.length > 0,
            program_count: criteria.programIds.length,
            program_ids: [...criteria.programIds]
                .sort((a, b) => a - b)
                .join(',')
        });
    }

    /**
     * Sorting re-runs the search with whatever is in the search boxes, so text or an address the
     * student typed but never submitted is applied here and reported as used.
     *
     * @param sortOrder the language-neutral sort key: `name`, `-name` or `distance`.
     * @param searchTerm the text in the name/description box; only whether it is present is sent.
     * @param searchedByAddress sorting by distance only means something once an address is
     * entered, so reports read the two together.
     */
    public providerSort(
        sortOrder: string,
        searchTerm: string,
        searchedByAddress: boolean
    ) {
        gtag('event', 'provider_sort', {
            ...this.getCommonProperties(),
            sort_order: sortOrder,
            searched_by_text: searchTerm.trim().length > 0,
            searched_by_address: searchedByAddress
        });
    }

    public click(pageTitle: string = '', eventName: string = '') {
        gtag('event', 'click', {
            ...this.getCommonProperties(),
            page_title: pageTitle,
            event_name: eventName
        });
    }

    public login() {
        gtag('event', 'login', this.getCommonProperties());
    }

    public signUp() {
        gtag('event', 'sign_up', this.getCommonProperties());
    }

    public formError(form: UntypedFormGroup) {
        gtag('event', 'form_error', {
            ...this.getCommonProperties(),
            ...this.getFormValidationSummary(form)
        });
    }

    private getCommonProperties(): any {
        return {
            client: runtimeConfig.clientCode?.toUpperCase(),
            // The per-build Angular environment, not runtimeconfig.js, which is no longer
            // rewritten per deployment and so reported "Dev" from every environment.
            environment: environment.environment,
            language: sessionStorage.getItem('selectedLanguage') ?? 'en'
        };
    }

    /**
     * Summarises a failed validation as the names of the failing controls and the kinds of
     * failure — never the values.
     *
     * Two things are deliberately excluded. `form.getRawValue()`, which used to be sent whole,
     * carries everything the student typed: SSN, date of birth, income, housing status. And the
     * Angular error objects themselves are unsafe: `pattern`, `min` and `max` embed the user's
     * input as `actualValue` / `actual`, and all three are wired up from the server-driven schema
     * in core/helpers/validator-registry.ts. Only the error keys are reported.
     *
     * The keys alone are what makes the report actionable anyway — "which field fails, and how
     * often" is a question you can chart; a free-text value is not.
     */
    private getFormValidationSummary(form: UntypedFormGroup): any {
        const invalidFields: string[] = [];
        const errorTypes = new Set<string>();

        Object.keys(form.controls).forEach((key) => {
            const controlErrors = form.get(key)?.errors;
            if (!controlErrors) return;

            invalidFields.push(key);
            Object.keys(controlErrors).forEach((errorKey) =>
                errorTypes.add(errorKey)
            );
        });

        return {
            invalid_fields: invalidFields.join(','),
            invalid_field_count: invalidFields.length,
            error_types: Array.from(errorTypes).join(','),
            form_pristine: form.pristine,
            form_touched: form.touched
        };
    }
}
